import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import type { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { EducationLevel } from 'utils/constants/general/educationLevels';
import { ProfessionalLevel } from 'utils/constants/general/professionalLevels';
import { humanDate } from 'utils/date';
import { isDefined } from 'utils/functional/type';
import { getEducationLevelName, getProfessionalLevelName } from 'utils/job';
import { formatSalaryRangeWithPeriodHumanName } from 'utils/listings';

// TODO: reference the remaining ApiJob fields
type Props = {
  orgAreasOfFocus?: ApiAreaOfFocus[];
  applicationDeadline: string | null;
  education: EducationLevel;
  salaryMinimum: number | null;
  salaryMaximum: number | null;
  salaryCurrency: string | null;
  salaryPeriod: string | null;
  salaryDetails: string | null;
  professionalLevel: ProfessionalLevel;
  jobCountry: string;
  areasOfFocus: ApiAreaOfFocus[];
} & Pick<
  ApiJob,
  'startDate' | 'endDate' | 'isFullTime' | 'isTemporary' | 'isContract'
>;

export function JobDetailsList({
  orgAreasOfFocus,
  education,
  isContract,
  isFullTime,
  isTemporary,
  startDate,
  endDate,
  applicationDeadline,
  professionalLevel,
  salaryCurrency,
  salaryDetails,
  salaryMaximum,
  salaryMinimum,
  salaryPeriod,
  jobCountry,
  areasOfFocus,
}: Props) {
  let allAreasOfFocus: ApiAreaOfFocus[] = [...areasOfFocus];
  if (orgAreasOfFocus) {
    allAreasOfFocus.push(...orgAreasOfFocus);
  }
  allAreasOfFocus = [...new Set(allAreasOfFocus)];

  return (
    <DetailsList>
      {isFullTime && !isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="clock"
          value={getText('Full Time')}
        />
      )}
      {!isFullTime && !isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="part-pie"
          value={getText('Part Time')}
        />
      )}
      {isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="paper"
          value={getText('Contract / Freelance')}
        />
      )}
      {isTemporary && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="hourglass"
          value={getText('Temporary')}
        />
      )}
      {startDate && (
        <DetailsListItem
          title={getText('Start Date')}
          iconName="calendar"
          value={humanDate(startDate)}
        />
      )}
      {endDate && (
        <DetailsListItem
          title={getText('End Date')}
          iconName="calendar"
          value={humanDate(endDate)}
        />
      )}
      {applicationDeadline && (
        <DetailsListItem
          title={getText('Application Deadline')}
          iconName="hourglass"
          value={humanDate(applicationDeadline)}
        />
      )}
      {education !== EducationLevel.NO_REQUIREMENT && (
        <DetailsListItem
          title={getText('Education')}
          iconName="graduation-cap"
          value={getEducationLevelName(education)}
        />
      )}
      {professionalLevel !== ProfessionalLevel.NONE && (
        <DetailsListItem
          title={getText('Experience Level')}
          iconName="stairs"
          value={getProfessionalLevelName(professionalLevel)}
        />
      )}
      {(Number(salaryMinimum) ||
        Number(salaryMaximum) ||
        isDefined(salaryDetails)) && (
        <DetailsListItem
          title={
            salaryPeriod === 'YEAR'
              ? getText('Salary')
              : getText('Compensation')
          }
          qaId="listing-compensation"
          iconName="money"
          tooltipText={getText(
            'Idealist encourages all organizations to include salary ranges when possible. Please consider this range a "best estimate" rather than a firm or final offer.',
          )}
          value={
            isDefined(salaryMinimum) || isDefined(salaryMaximum)
              ? formatSalaryRangeWithPeriodHumanName(
                  salaryMinimum,
                  salaryMaximum,
                  jobCountry,
                  salaryCurrency,
                  // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
                  salaryPeriod,
                )
              : getText('See below')
          }
          additionalText={salaryDetails}
        />
      )}
      {allAreasOfFocus.length > 0 && (
        <DetailsListItem
          iconName="globe"
          title={getText('Cause Areas')}
          value={allAreasOfFocus
            ?.map((item) => AREAS_OF_FOCUS[item])
            .join(', ')}
        />
      )}
    </DetailsList>
  );
}
